<template>
  <v-card outlined>
    <v-card-title class="subtitle-1 primary--text">
      <v-icon left>mdi-gauge</v-icon>
      Efficiency Metrics
    </v-card-title>

    <v-card-text>
      <!-- Input Summary -->
      <v-row class="mb-4">
        <v-col cols="12">
          <div class="text-subtitle-2 mb-2">Input Data</div>
          <v-row>
            <v-col cols="12" md="4">
              <div class="text-caption grey--text">DG Consumption</div>
              <div class="text-body-1">{{ formattedDgConsumption }} kWh</div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="text-caption grey--text">Diesel Consumption</div>
              <div class="text-body-1">{{ formattedDieselConsumption }} L</div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="text-caption grey--text">Running Hours</div>
              <div class="text-body-1">{{ formattedRunningHours }} hrs</div>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-divider class="mb-4" />

      <!-- Efficiency Calculations -->
      <v-row>
        <!-- kWh per Litre Efficiency -->
        <v-col cols="12" md="6">
          <v-card outlined :color="kwhEfficiencyColor" class="pa-3">
            <div class="d-flex align-center mb-2">
              <v-icon :color="kwhEfficiencyIconColor" class="mr-2">
                {{ kwhEfficiencyIcon }}
              </v-icon>
              <div class="text-subtitle-1 font-weight-medium">
                Power Generation Efficiency
              </div>
            </div>

            <div class="text-h4 font-weight-bold mb-1" :class="kwhEfficiencyTextColor">
              {{ formattedKwhPerLitre }}
            </div>

            <div class="text-caption grey--text mb-2">
              kWh per Litre
            </div>

            <div class="text-caption">
              <span v-if="dieselConsumption > 0">
                {{ formattedDgConsumption }} kWh ÷ {{ formattedDieselConsumption }} L
              </span>
              <span v-else class="warning--text">
                <v-icon small color="warning">mdi-alert</v-icon>
                No diesel consumption data
              </span>
            </div>

            <!-- Efficiency Range Indicator -->
            <div class="mt-2">
              <div class="text-caption mb-1">Performance Range:</div>
              <v-chip
                small
                :color="kwhEfficiencyRangeColor"
                :text-color="kwhEfficiencyRangeTextColor"
              >
                {{ kwhEfficiencyRange }}
              </v-chip>
            </div>
          </v-card>
        </v-col>

        <!-- Fuel consumption (Litres per Hour) -->
        <v-col cols="12" md="6">
          <v-card outlined :color="fuelConsumptionColor" class="pa-3">
            <div class="d-flex align-center mb-2">
              <v-icon :color="fuelConsumptionIconColor" class="mr-2">
                {{ fuelConsumptionIcon }}
              </v-icon>
              <div class="text-subtitle-1 font-weight-medium">
                Fuel Consumption Rate
              </div>
            </div>

            <div class="text-h4 font-weight-bold mb-1" :class="fuelConsumptionTextColor">
              {{ formattedLitresPerHour }}
            </div>

            <div class="text-caption grey--text mb-2">
              Litres per Hour
            </div>

            <div class="text-caption">
              <span v-if="runningHours > 0">
                {{ formattedDieselConsumption }} L ÷ {{ formattedRunningHours }} hrs
              </span>
              <span v-else class="warning--text">
                <v-icon small color="warning">mdi-alert</v-icon>
                No running hours data
              </span>
            </div>

            <!-- Efficiency Range Indicator -->
            <div class="mt-2">
              <div class="text-caption mb-1">Performance Range:</div>
              <v-chip
                small
                :color="fuelConsumptionRangeColor"
                :text-color="fuelConsumptionRangeTextColor"
              >
                {{ fuelConsumptionRange }}
              </v-chip>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Division by Zero Warning -->
      <v-row v-if="showDivisionByZeroWarning">
        <v-col cols="12">
          <v-alert
            type="warning"
            outlined
            dense
            class="mt-3"
          >
            <div class="d-flex align-center">
              <div class="flex-grow-1">
                <v-icon class="mr-2">mdi-alert-circle</v-icon>
                <strong>Unable to calculate efficiency metrics:</strong>
                Diesel consumption must be greater than zero to calculate efficiency ratios.
              </div>
            </div>
            <div class="mt-2 text-caption">
              <strong>Tip:</strong>
              Enter the actual diesel consumption in litres to see efficiency calculations.
            </div>
          </v-alert>
        </v-col>
      </v-row>

      <!-- Calculation Error Warning -->
      <v-row v-if="hasCalculationErrors">
        <v-col cols="12">
          <v-alert
            type="error"
            outlined
            dense
            class="mt-3"
          >
            <template #prepend>
              <v-icon>mdi-alert-circle</v-icon>
            </template>
            <strong>Calculation Error:</strong>
            Invalid input values detected. Please check your data and try again.
            <div class="mt-2 text-caption">
              Ensure all values are positive numbers and within reasonable ranges.
            </div>
          </v-alert>
        </v-col>
      </v-row>

      <!-- Additional Information -->
      <v-row v-if="dieselConsumption > 0" class="mt-3">
        <v-col cols="12">
          <v-expansion-panels flat>
            <v-expansion-panel>
              <v-expansion-panel-header class="text-caption">
                <v-icon small class="mr-2">mdi-information</v-icon>
                Efficiency Guidelines
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="text-caption">
                  <div class="mb-2">
                    <strong>Power Generation Efficiency (kWh/L):</strong>
                  </div>
                  <ul class="mb-3">
                    <li>Excellent: &gt; 3.5 kWh/L</li>
                    <li>Good: 3.0 - 3.5 kWh/L</li>
                    <li>Average: 2.5 - 3.0 kWh/L</li>
                    <li>Poor: &lt; 2.5 kWh/L</li>
                  </ul>

                  <div class="mb-2">
                    <strong>Fuel Consumption Rate (L/hr):</strong>
                  </div>
                  <ul>
                    <li>Excellent: &lt; 2.0 L/hr</li>
                    <li>Good: 2.0 - 3.0 L/hr</li>
                    <li>Average: 3.0 - 4.0 L/hr</li>
                    <li>Poor: &gt; 4.0 L/hr</li>
                  </ul>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { DGEfficiencyMetrics, DGBillingUtils } from '../../types';

export default {
  name: 'DGEfficiencyMetrics',

  props: {
    dgConsumption: {
      type: Number,
      default: 0,
      validator: (value) => value >= 0,
    },
    dieselConsumption: {
      type: Number,
      default: 0,
      validator: (value) => value >= 0,
    },
    runningHours: {
      type: Number,
      default: 0,
      validator: (value) => value >= 0,
    },
    decimalPlaces: {
      type: Number,
      default: 2,
      validator: (value) => value >= 0 && value <= 10,
    },
  },

  computed: {
    // Calculated efficiency metrics
    kwhPerLitre() {
      return DGEfficiencyMetrics.calculateKwhPerLitre(this.dgConsumption, this.dieselConsumption);
    },

    litresPerHour() {
      return DGEfficiencyMetrics.calculateLitresPerHour(this.dieselConsumption, this.runningHours);
    },

    // Formatted display values
    formattedDgConsumption() {
      return DGBillingUtils.formatEfficiencyMetric(this.dgConsumption, 1);
    },

    formattedDieselConsumption() {
      return DGBillingUtils.formatEfficiencyMetric(this.dieselConsumption, 1);
    },

    formattedRunningHours() {
      return DGBillingUtils.formatEfficiencyMetric(this.runningHours, 1);
    },

    formattedKwhPerLitre() {
      return DGBillingUtils.formatEfficiencyMetric(this.kwhPerLitre, this.decimalPlaces);
    },

    formattedLitresPerHour() {
      return DGBillingUtils.formatEfficiencyMetric(this.litresPerHour, this.decimalPlaces);
    },

    // Division by zero handling
    showDivisionByZeroWarning() {
      return this.dieselConsumption === 0 && (this.dgConsumption > 0 || this.runningHours > 0);
    },

    // Calculation error detection
    hasCalculationErrors() {
      return (
        (this.dgConsumption < 0)
        || (this.dieselConsumption < 0)
        || (this.runningHours < 0)
        || (Number.isNaN(this.dgConsumption))
        || (Number.isNaN(this.dieselConsumption))
        || (Number.isNaN(this.runningHours))
        || (!Number.isFinite(this.kwhPerLitre) && this.dieselConsumption > 0)
        || (!Number.isFinite(this.litresPerHour) && this.runningHours > 0)
      );
    },

    // kWh per Litre efficiency indicators
    kwhEfficiencyRange() {
      if (this.dieselConsumption === 0) return 'N/A';
      if (this.kwhPerLitre > 3.5) return 'Excellent';
      if (this.kwhPerLitre >= 3.0) return 'Good';
      if (this.kwhPerLitre >= 2.5) return 'Average';
      return 'Poor';
    },

    kwhEfficiencyRangeColor() {
      if (this.dieselConsumption === 0) return 'grey';
      if (this.kwhPerLitre > 3.5) return 'success';
      if (this.kwhPerLitre >= 3.0) return 'info';
      if (this.kwhPerLitre >= 2.5) return 'warning';
      return 'error';
    },

    kwhEfficiencyRangeTextColor() {
      return 'white';
    },

    kwhEfficiencyColor() {
      if (this.dieselConsumption === 0) return 'grey lighten-4';
      if (this.kwhPerLitre > 3.5) return 'success lighten-5';
      if (this.kwhPerLitre >= 3.0) return 'info lighten-5';
      if (this.kwhPerLitre >= 2.5) return 'warning lighten-5';
      return 'error lighten-5';
    },

    kwhEfficiencyTextColor() {
      if (this.dieselConsumption === 0) return 'grey--text';
      if (this.kwhPerLitre > 3.5) return 'success--text';
      if (this.kwhPerLitre >= 3.0) return 'info--text';
      if (this.kwhPerLitre >= 2.5) return 'warning--text';
      return 'error--text';
    },

    kwhEfficiencyIconColor() {
      if (this.dieselConsumption === 0) return 'grey';
      if (this.kwhPerLitre > 3.5) return 'success';
      if (this.kwhPerLitre >= 3.0) return 'info';
      if (this.kwhPerLitre >= 2.5) return 'warning';
      return 'error';
    },

    kwhEfficiencyIcon() {
      if (this.dieselConsumption === 0) return 'mdi-help-circle';
      if (this.kwhPerLitre > 3.5) return 'mdi-trending-up';
      if (this.kwhPerLitre >= 3.0) return 'mdi-trending-neutral';
      if (this.kwhPerLitre >= 2.5) return 'mdi-trending-down';
      return 'mdi-alert-circle';
    },

    // Run Hour per Litre efficiency indicators
    fuelConsumptionRange() {
      if (this.runningHours === 0) return 'N/A';
      if (this.litresPerHour < 2.0) return 'Excellent';
      if (this.litresPerHour < 3.0) return 'Good';
      if (this.litresPerHour < 4.0) return 'Average';
      return 'Poor';
    },

    fuelConsumptionRangeColor() {
      if (this.runningHours === 0) return 'grey';
      if (this.litresPerHour < 2.0) return 'success';
      if (this.litresPerHour < 3.0) return 'info';
      if (this.litresPerHour < 4.0) return 'warning';
      return 'error';
    },

    fuelConsumptionRangeTextColor() {
      return 'white';
    },

    fuelConsumptionColor() {
      if (this.runningHours === 0) return 'grey lighten-4';
      if (this.litresPerHour < 2.0) return 'success lighten-5';
      if (this.litresPerHour < 3.0) return 'info lighten-5';
      if (this.litresPerHour < 4.0) return 'warning lighten-5';
      return 'error lighten-5';
    },

    fuelConsumptionTextColor() {
      if (this.runningHours === 0) return 'grey--text';
      if (this.litresPerHour < 2.0) return 'success--text';
      if (this.litresPerHour < 3.0) return 'info--text';
      if (this.litresPerHour < 4.0) return 'warning--text';
      return 'error--text';
    },

    fuelConsumptionIconColor() {
      if (this.runningHours === 0) return 'grey';
      if (this.litresPerHour < 2.0) return 'success';
      if (this.litresPerHour < 3.0) return 'info';
      if (this.litresPerHour < 4.0) return 'warning';
      return 'error';
    },

    fuelConsumptionIcon() {
      if (this.runningHours === 0) return 'mdi-help-circle';
      if (this.litresPerHour < 2.0) return 'mdi-trending-up';
      if (this.litresPerHour < 3.0) return 'mdi-trending-neutral';
      if (this.litresPerHour < 4.0) return 'mdi-trending-down';
      return 'mdi-alert-circle';
    },
  },

  watch: {
    // Emit efficiency metrics when they change
    kwhPerLitre: {
      handler(newValue) {
        try {
          // Validate the calculated value
          if (Number.isNaN(newValue) || !Number.isFinite(newValue)) {
            console.warn('Invalid kWh per litre calculation:', newValue);
            return;
          }

          this.$emit('efficiency-calculated', {
            kwhPerLitre: newValue,
            litresPerHour: this.litresPerHour,
          });
        } catch (error) {
          console.error('Error emitting efficiency calculation:', error);
        }
      },
      immediate: true,
    },

    litresPerHour: {
      handler(newValue) {
        try {
          // Validate the calculated value
          if (Number.isNaN(newValue) || !Number.isFinite(newValue)) {
            console.warn('Invalid litres per hour calculation:', newValue);
            return;
          }

          this.$emit('efficiency-calculated', {
            kwhPerLitre: this.kwhPerLitre,
            litresPerHour: newValue,
          });
        } catch (error) {
          console.error('Error emitting efficiency calculation:', error);
        }
      },
    },

    // Watch for input validation errors
    dieselConsumption: {
      handler(newValue) {
        if (newValue < 0) {
          this.$emit('validation-error', 'Diesel consumption cannot be negative');
        } else if (newValue > 20000) {
          this.$emit('validation-warning', 'Diesel consumption seems unusually high');
        }
      },
    },

    runningHours: {
      handler(newValue) {
        if (newValue < 0) {
          this.$emit('validation-error', 'Running hours cannot be negative');
        } else if (newValue > 744) {
          this.$emit('validation-error', 'Running hours cannot exceed 744 hours per month');
        }
      },
    },

    dgConsumption: {
      handler(newValue) {
        if (newValue < 0) {
          this.$emit('validation-error', 'DG consumption cannot be negative');
        }
      },
    },
  },

  methods: {
    // Validate input values
    validateInputs() {
      const errors = [];

      const dieselValidation = DGBillingUtils.validateDieselConsumption(this.dieselConsumption);
      if (!dieselValidation.isValid && this.dieselConsumption !== 0) {
        errors.push(...dieselValidation.errors);
      }

      const hoursValidation = DGBillingUtils.validateRunningHours(this.runningHours);
      if (!hoursValidation.isValid && this.runningHours !== 0) {
        errors.push(...hoursValidation.errors);
      }

      if (typeof this.dgConsumption !== 'number' || this.dgConsumption < 0) {
        errors.push('DG consumption must be a non-negative number');
      }

      return {
        isValid: errors.length === 0,
        errors,
      };
    },

    // Get efficiency metrics object
    getEfficiencyMetrics() {
      return {
        kwhPerLitre: this.kwhPerLitre,
        litresPerHour: this.litresPerHour,
        formatted: {
          kwhPerLitre: this.formattedKwhPerLitre,
          litresPerHour: this.formattedLitresPerHour,
        },
      };
    },
  },
};
</script>

<style scoped>
.v-card--outlined {
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
}

.v-expansion-panel-content >>> .v-expansion-panel-content__wrap {
  padding: 8px 16px 16px;
}

.v-expansion-panel-header {
  min-height: 36px !important;
  padding: 8px 16px !important;
}

.v-chip.v-size--small {
  font-size: 0.75rem !important;
}
</style>
